/**
 * @since 2025-02-19
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import axios from "axios";
import { getAuth } from "firebase/auth";
import { getConfig } from "./getConfig";

const { BACKEND_HTTP_URL } = getConfig();

// Custom error class for authentication failures
class AuthenticationError extends Error {
  constructor(message) {
    super(message);
    this.name = 'AuthenticationError';
  }
}

// Helper: perform POST with Bearer auth, handling both Firebase and Azure token expiration
const postWithAuth = async (url, payload, idToken, getToken = null) => {
  const makeRequest = async (token) =>
    axios.post(url, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

  try {
    const response = await makeRequest(idToken);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 401) {
      // Try Azure token refresh via getToken callback (force refresh)
      if (getToken) {
        try {
          // Retry even when the token comes back unchanged: a still-valid
          // token means the 401 was transient (backend blip), and a single
          // retry avoids treating it as session expiry.
          const freshToken = await getToken(true);
          if (freshToken) {
            const retryResponse = await makeRequest(freshToken);
            return retryResponse.data;
          }
        } catch (refreshErr) {
          console.error("Token refresh via getToken failed:", refreshErr);
        }
      }

      // Fallback: try Firebase token refresh
      const auth = getAuth();
      if (auth.currentUser) {
        try {
          const freshToken = await auth.currentUser.getIdToken(true);
          const retryResponse = await makeRequest(freshToken);
          return retryResponse.data;
        } catch (refreshErr) {
          console.error("Firebase token refresh failed:", refreshErr);
        }
      }

      throw new AuthenticationError("Session expired. Please log in again.");
    }
    throw error;
  }
};

const getLogList = async (schedulerName, jobName, idToken, getToken = null) => {
  return postWithAuth(
    `${BACKEND_HTTP_URL}/api/getLogList/`,
    { schedulerName, jobName },
    idToken,
    getToken
  );
};

const setStatus = async (listRequests, idToken, getToken = null) => {
  return postWithAuth(
    `${BACKEND_HTTP_URL}/api/setStatus/`,
    { listRequests },
    idToken,
    getToken
  );
};

export { getLogList, setStatus, AuthenticationError };
